import { Autocomplete, InputLabel, OutlinedInput, Stack } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import UploadIcon from "@mui/icons-material/Upload";
import { useToggle } from "../../../Utils/Hooks/useToggle";
import { useEffect } from "react";
import { DbIngestConfigPackPlaceholderRowInfos } from "../../../Core/Api/Types/db-ingest-config-pack-placeholder-row-infos";
import { GetEnumValueLabelMap } from "../../../Utils/helpers";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import TableCellStringExpend from "../../Table/TableCellStringExpend/table-cell-string-expend";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ingestService } from "../../../Core/Api/services";
import DataTable, { CellSize } from "../../Table/table";
import { DbIngestConfigPackInfos } from "../../../Core/Api/Types/db-ingest-config-pack-infos";
import TableSkeleton from "../../Table/TableSkeleton";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";

export type ModalFindPlaceholderCloseProps = {
    id?: number;
};

export type ModalFindPlaceholderElementProps = {
    onClose?: (props: ModalFindPlaceholderCloseProps) => void;
    packId: number;
    objectType: ObjectType;
};

export type ModalFindPlaceholderProps = CommonModalProps<ModalFindPlaceholderElementProps>;

const ModalFindPlaceholder = React.forwardRef<any, ModalFindPlaceholderProps>((props, ref) => {
    const { t } = useTranslation();
    const { closeModal, onClose, packId, objectType } = props;
    const [isLoading, setLoading] = React.useState(true);

    const [packs, setPacks] = React.useState<DbIngestConfigPackInfos[]>([]);
    const [selectedPack, setSelectedPack] = React.useState<DbIngestConfigPackInfos | null>(null);

    const [placeholders, setPlaceholders] = React.useState<DbIngestConfigPackPlaceholderRowInfos[]>([]);

    const [filterText, setFilterText] = React.useState("");

    const columnsAction: GridColDef[] = [
        {
            field: "id",
            headerName: "Id",
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
        },
        {
            field: "name",
            headerName: t("common:name"),
            minWidth: CellSize.xxlarge,
            flex: CellSize.xxlarge,
            renderCell: (data: any) => <TableCellStringExpend data={data} />,
        },
        {
            field: "description",
            headerName: t("common:description"),
            minWidth: CellSize.xxlarge,
            flex: CellSize.xxlarge,
            renderCell: (data: any) => <TableCellStringExpend data={data} />,
        },
        {
            field: "objectType",
            headerName: t("common:objectType"),
            type: "singleSelect",
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            valueOptions: GetEnumValueLabelMap(ObjectType),
            renderCell: (data: any) => <AssetTypeChip type={data.value} />,
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            minWidth: CellSize.actionsDefault,
            flex: CellSize.actionsDefault,
            align: "right",

            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<UploadIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                    label={t("common:import")}
                    onClick={() => {
                        onClickImport(params?.row?.id);
                    }}
                />,
            ],
        },
    ];

    const onClickImport = (id: number) => {
        onClose && onClose({ id: id });
        closeModal();
    };

    const handleFilter = (filter: string) => {
        setFilterText(filter);
    };

    useEffect(() => {
        setLoading(true);
        ingestService.GetConfigPackList().then((response) => {
            setPacks(response.packs || []);
            setSelectedPack(response.packs?.find((p) => p.id === packId) || null);
        });
        ingestService.GetPlaceholdersByPackId(packId).then((response) => {
            if (response.pack?.placeholderRows) {
                setPlaceholders(response.pack?.placeholderRows!.filter((x) => x.objectType === objectType));
            }
            setLoading(false);
        });
    }, [packId]);

    useEffect(() => {
        if (selectedPack) {
            setLoading(true);
            ingestService.GetPlaceholdersByPackId(selectedPack.id).then((response) => {
                if (response.pack?.placeholderRows) {
                    setPlaceholders(response.pack?.placeholderRows!.filter((x) => x.objectType === objectType));
                }
                setLoading(false);
            });
        }
    }, [selectedPack]);

    return (
        <Modal title={t("modal:title.importConfigurations")} reference={ref} closeModal={closeModal} width={1200}>
            <Stack spacing={2}>
                <Stack spacing={1}>
                    <InputLabel>{t("common:selectPack")}</InputLabel>
                    <Autocomplete
                        size="medium"
                        options={packs}
                        //sx={{ flex: 1, mb: 1 }}
                        value={selectedPack}
                        onChange={(_, value) => setSelectedPack(value)}
                        getOptionLabel={(value: any) => value.name}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        popupIcon={<ExpandMoreRoundedIcon />}
                        renderInput={(params) => (
                            <OutlinedInput
                                {...params.InputProps}
                                inputProps={params.inputProps}
                                placeholder={t("common:selectPack")}
                                sx={{ width: "100%" }}
                            />
                        )}
                    />
                </Stack>
                <Stack spacing={1}>
                    <OutlinedInput
                        placeholder={t("field:placeholder.searchPlaceholders")}
                        endAdornment={<SearchIcon />}
                        onChange={(e) => handleFilter(e.target.value)}
                    />
                    {isLoading ? (
                        <TableSkeleton />
                    ) : (
                        <DataTable
                            noControls
                            rows={
                                placeholders?.filter(
                                    (x) =>
                                        filterText === "" ||
                                        x.name?.includesAllWords(filterText) ||
                                        x.description?.includesAllWords(filterText) ||
                                        x.id?.toString().includesAllWords(filterText)
                                )!
                            }
                            columns={columnsAction}
                            rowHeight={38}
                            sx={{ marginTop: 2 }}
                        />
                    )}
                </Stack>
            </Stack>
        </Modal>
    );
});

export default ModalFindPlaceholder;
